import { useState, useRef, useCallback } from "react"
import OpenAI from "openai"
import { toast } from "sonner"
import { app } from "@/lib/app"

interface UseVoiceRecordingOptions {
  teamId: string | undefined
  onTranscription: (text: string) => void
  disabled?: boolean
}

interface UseVoiceRecordingReturn {
  isRecording: boolean
  recordingTime: number
  isTranscribing: boolean
  formatRecordingTime: (seconds: number) => string
  startRecording: () => Promise<void>
  stopRecording: () => void
  cancelRecording: () => void
}

export function useVoiceRecording({
  teamId,
  onTranscription,
  disabled,
}: UseVoiceRecordingOptions): UseVoiceRecordingReturn {
  const [isRecording, setIsRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const recordingCancelledRef = useRef(false)

  const formatRecordingTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }, [])

  const startRecording = useCallback(async (): Promise<void> => {
    if (disabled || !teamId) return
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" })
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []
      recordingCancelledRef.current = false

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop())
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current)
          recordingTimerRef.current = null
        }
        setRecordingTime(0)

        if (recordingCancelledRef.current) {
          audioChunksRef.current = []
          return
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" })
        const audioFile = new File([audioBlob], "audio.webm", { type: "audio/webm" })

        setIsTranscribing(true)
        try {
          const apiKeys = await app.fetchAPIkeys(teamId)
          const openai = new OpenAI({ apiKey: apiKeys.openai, dangerouslyAllowBrowser: true })
          const response = await openai.audio.transcriptions.create({
            file: audioFile,
            model: "whisper-1",
          })
          const text = response.text?.trim()
          if (text) {
            onTranscription(text)
          } else {
            toast("Transcription Empty", {
              description: "No speech detected in the recording.",
              action: { label: "Ok", onClick: () => {} },
            })
          }
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : String(err)
          toast("Transcription Error", {
            description: message,
            action: { label: "Ok", onClick: () => {} },
          })
        } finally {
          setIsTranscribing(false)
        }
      }

      mediaRecorder.start()
      setIsRecording(true)
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1)
      }, 1000)
    } catch (err) {
      toast("Microphone Error", {
        description: "Could not access microphone. Please check permissions.",
        action: { label: "Ok", onClick: () => {} },
      })
    }
  }, [teamId, onTranscription, disabled])

  const stopRecording = useCallback((): void => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      recordingCancelledRef.current = false
      mediaRecorderRef.current.stop()
      setIsRecording(false)
    }
  }, [])

  const cancelRecording = useCallback((): void => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      recordingCancelledRef.current = true
      mediaRecorderRef.current.stop()
      setIsRecording(false)
    }
  }, [])

  return {
    isRecording,
    recordingTime,
    isTranscribing,
    formatRecordingTime,
    startRecording,
    stopRecording,
    cancelRecording,
  }
}
